import { useQuery } from '@tanstack/react-query';

import { adminFetch } from '@/hooks/admin/useAdminApi';

export interface GroupServiceJob {
  customer_id: string;
  store_name: string;
  store_code: string | null;
  is_master: boolean;
  job_id: string;
  status: string | null;
  description: string | null;
  category: string | null;
  completed_at: string | null;
  technician_name: string | null;
  location: string | null;
  is_open: boolean;
}

interface Page {
  ok: true;
  is_group: boolean;
  jobs: GroupServiceJob[];
  stores_read?: number;
  stores_total?: number;
  remaining: number;
  next_after?: string | null;
  unmapped?: string[];
  failed?: Array<{ store_name: string; error: string }>;
}

export interface GroupServiceJobsResult {
  isGroup: boolean;
  jobs: GroupServiceJob[];
  storesTotal: number;
  /** Stores with no Service Fusion link — fixable from their Locations tab. */
  unmapped: string[];
  failed: Array<{ store_name: string; error: string }>;
  /** True when the sweep ran out of passes before finishing the roster. */
  truncated: boolean;
}

/** Hard stop on the loop. 26 stores fit in ~2 passes; 8 is generous. */
const MAX_PASSES = 8;

/**
 * Service Fusion visit history across every store in the chain.
 *
 * ⚠ The endpoint is BUDGETED — SF has no bulk-by-parent read, so it is one
 * live call per store and it hands back `next_after` when its time is up.
 * This hook drives that loop to completion rather than showing a partial
 * roster, because "no jobs at Folsom" and "we never got to Folsom" look
 * identical on screen and only one of them is true.
 */
export function useGroupServiceJobs(
  days = 90,
  status: 'all' | 'open' | 'completed' = 'all',
  // ⚠ Off by default. This is 26 live Service Fusion calls; it must not fire
  // because a page mounted, only because someone asked to see visits.
  enabled = true,
) {
  return useQuery<GroupServiceJobsResult>({
    enabled,
    queryKey: ['group-service-jobs', days, status],
    queryFn: async () => {
      const jobs: GroupServiceJob[] = [];
      const unmapped: string[] = [];
      const failed: Array<{ store_name: string; error: string }> = [];
      let after: string | null = null;
      let storesTotal = 0;
      let isGroup = false;
      let truncated = false;

      for (let pass = 0; pass < MAX_PASSES; pass += 1) {
        const qs = new URLSearchParams({ days: String(days), status });
        if (after) qs.set('after', after);
        const page: Page = await adminFetch<Page>(`customer-group-service-jobs?${qs}`);

        isGroup = page.is_group;
        if (!page.is_group) break;

        jobs.push(...(page.jobs ?? []));
        unmapped.push(...(page.unmapped ?? []));
        failed.push(...(page.failed ?? []));
        storesTotal = page.stores_total ?? storesTotal;

        if (page.remaining > 0 && page.next_after) {
          after = page.next_after;
          if (pass === MAX_PASSES - 1) truncated = true;
        } else {
          break;
        }
      }

      return { isGroup, jobs, storesTotal, unmapped, failed, truncated };
    },
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}
